import { Box, Divider, Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Option } from '@spike/model';
import { ClientTags } from '@spike/client-model';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getClientTagsThunk, MarketplaceStatus, saveMarketplaceThunk } from '@spike/marketplace-action';
import { MarketplaceClientTag, MarketplaceClientTags } from '@spike/marketplace-model';
import { RootState } from 'store';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import useNonInitialEffect from '@versiondos/hooks';
import { showError } from '@spike/notifications-action';
import { setTimeZone } from 'actions/login/LoginActions';
import Chips from './Chips';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface TagsProps {
    disabled?: boolean;
    tags: ClientTags;
    onChange?: (tags: ClientTags) => void;
}

const useStyles = makeStyles(() =>
    createStyles({
        container: {
            overflow: 'visible'
        },
        titleContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        title: {
            fontSize: 16,
            lineHeight: 1,
            fontWeight: 500,
            marginBottom: 24
        },
        divider: {
            'margin': '24px 0px',

            '& hr': {
                backgroundColor: '#D4D4D4'
            }
        },
        tagsContainer: {
            overflow: 'visible'
        },
        containerLink: {
            'display': 'flex',
            'flexDirection': 'row',
            'alignItems': 'center',
            'textDecoration': 'none',
            'borderBottom': '2px solid #222',
            'cursor': 'pointer',
            'color': '#222222',
            'height': '30px',
            'marginBottom': '24px',

            '& > svg': {
                marginRight: '8px'
            },

            '&:hover': {
                borderBottom: '2px solid #92B4A7',
                color: '#92B4A7'
            }
        },
        manageLink: {
            fontWeight: 500,
            fontSize: '15px',
            color: '#222222'
        }
    })
);

export const Tags: FunctionComponent<TagsProps> = props => {
    const classes = useStyles();
    const marketplace = useMarketplace();

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const status = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);
    const store = useSelector<RootState, MarketplaceClientTags>(state => state.marketplace.clientTags);

    const [positives, setPositives] = useState<Option<string>[]>([]);
    const [negatives, setNegatives] = useState<Option<string>[]>([]);

    const [marketplacePositives, setMarketplacePositives] = useState([...marketplace.clientPositivePersonality]);
    const [marketplaceNegatives, setMarketplaceNegatives] = useState([...marketplace.clientNegativePersonality]);

    const [newTagName, setNewTagName] = useState('');

    useEffect(() => {
        dispatch(getClientTagsThunk(apiClientWrapper));
    }, []);

    useNonInitialEffect(() => {
        if (status === MarketplaceStatus.SaveSuccess) {
            setNewTagName('');
            dispatch(getClientTagsThunk(apiClientWrapper));
            setMarketplacePositives(marketplace.clientPositivePersonality);
            setMarketplaceNegatives(marketplace.clientNegativePersonality);
        }

        if (status === MarketplaceStatus.GetClientTagsSuccess) {
            setPositives(transformToOptions(sortedClientTags(store.positive)));
            setNegatives(transformToOptions(sortedClientTags(store.negative)));
        }
    }, [status]);

    const transformToOptions = (tags: MarketplaceClientTag[]): Option<string>[] => {
        return tags.map(tag => {
            return {
                id: tag.id,
                name: tag.name
            };
        });
    };

    const sortedClientTags = (clientTagList: MarketplaceClientTag[]) => {
        const getSourceOrder = (source: string) => {
            return source === 'default' ? 0 : 1;
        };

        return [...clientTagList].sort((a, b) => {
            const sourceOrderA = getSourceOrder(a.source);
            const sourceOrderB = getSourceOrder(b.source);

            if (sourceOrderA !== sourceOrderB) {
                return sourceOrderA - sourceOrderB;
            }

            return a.name.localeCompare(b.name);
        });
    };

    const areFieldsValid = (name: string, clientTagsDefault: Option<string>[]) => {
        const nameLower = name.toLowerCase();
        const nameWithUnderscore = nameLower.replace(/'/g, '').replace(/ /g, '_');
        const nameWithHyphen = nameLower.replace(/'/g, '').replace(/ /g, '-');

        const tagExists = clientTagsDefault.some(tag => {
            const tagIdLower = tag.id.toLowerCase();
            return tagIdLower === nameWithUnderscore || tagIdLower === nameWithHyphen;
        });

        return tagExists;
    };

    const addClientTagHandle = (clientTagCategory: string, name: string) => {
        if (name.trim() === '') {
            return;
        }

        const isPositiveTab = clientTagCategory === 'positive';
        const currentList = isPositiveTab ? marketplacePositives : marketplaceNegatives;
        const currentListAll = isPositiveTab ? positives : negatives;

        if (areFieldsValid(name, currentListAll)) {
            dispatch(showError('This tag is already added'));
            return;
        }

        const newTags = [...currentList, name];
        const newMarketplace = { ...marketplace };

        if (isPositiveTab) {
            newMarketplace.clientPositivePersonality = [...newTags];
        } else {
            newMarketplace.clientNegativePersonality = [...newTags];
        }

        dispatch(saveMarketplaceThunk(apiClientWrapper, newMarketplace, setTimeZone));
    };

    const normalizeTagId = (id: string): string => {
        return id.toLowerCase().replace(/'/g, '').replace(/ /g, '_');
    };

    const normalizeSelectedTags = (
        selectedTags: Array<Option<string>>,
        availableTags: Array<Option<string>>
    ): Array<Option<string>> => {
        return selectedTags
            .map(selectedTag => {
                const normalizedSelectedId = normalizeTagId(selectedTag.id);
                const matchingTag = availableTags.find(
                    availableTag =>
                        availableTag.id === selectedTag.id ||
                        normalizeTagId(availableTag.id) === normalizedSelectedId ||
                        availableTag.id === normalizedSelectedId
                );
                return matchingTag || selectedTag;
            })
            .filter(tag => availableTags.some(available => available.id === tag.id));
    };

    const changePositiveTags = (selectedTags: Array<Option<string>>) => {
        props.onChange && props.onChange({ ...props.tags, positive: selectedTags });
    };

    const changeNegativeTags = (selectedTags: Array<Option<string>>) => {
        props.onChange && props.onChange({ ...props.tags, negative: selectedTags });
    };

    const normalizedPositiveSelectedTags = normalizeSelectedTags(props.tags.positive, positives);
    const normalizedNegativeSelectedTags = normalizeSelectedTags(props.tags.negative, negatives);

    const manageLink = (
        <Link
            className={classes.containerLink}
            to="/business_settings/pets/tags?type=clients"
        >
            <Typography className={classes.manageLink}>Manage Tags</Typography>
        </Link>
    );

    return (
        <Grid className={classes.container}>
            <Box className={classes.titleContainer}>
                <Typography className={classes.title}>You can select multiple tags</Typography>
                {manageLink}
            </Box>
            <Grid
                id="clients_div_tags"
                container
                item
                xs={12}
                className={classes.tagsContainer}
            >
                <Chips
                    icon={<>👍</>}
                    tags={positives}
                    petTagCategory={'positive'}
                    selectedTags={normalizedPositiveSelectedTags}
                    onChangeSelection={changePositiveTags}
                    disabled={props.disabled}
                    showAddTagCustom={true}
                    newPetTagName={newTagName}
                    setNewPetTagName={setNewTagName}
                    onSavePetTagCustom={addClientTagHandle}
                />
                <Grid
                    item
                    xs={12}
                    className={classes.divider}
                >
                    <Divider />
                </Grid>
                <Chips
                    icon={<>👎</>}
                    tags={negatives}
                    petTagCategory={'negative'}
                    selectedTags={normalizedNegativeSelectedTags}
                    onChangeSelection={changeNegativeTags}
                    disabled={props.disabled}
                    showAddTagCustom={true}
                    newPetTagName={newTagName}
                    setNewPetTagName={setNewTagName}
                    onSavePetTagCustom={addClientTagHandle}
                />
            </Grid>
        </Grid>
    );
};

export default Tags;
